import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import styles from "./css/Contact.module.css";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";

export default function Contact() {
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    first_name: "",
    email: user?.email || "",
    phone: user?.phone || user?.number || "",
    subject: "",
    message: "",
    agree: false,
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      email: user?.email || "",
      phone: user?.phone || user?.number || "",
    }));
  }, [user]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "phone") return;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    let newErrors = {};

    if (!formData.first_name.trim())
      newErrors.first_name = "First name required";
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.message.trim()) newErrors.message = "Message required";
    if (!formData.agree) newErrors.agree = "You must accept Terms & Conditions";

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    const templateParams = {
      first_name: formData.first_name,
      last_name: "",
      email: formData.email.trim(),
      phone: formData.phone,
      country: "",
      subject: formData.subject,
      message: formData.message,
    };

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(
        (res) => {
          setFormData({
            first_name: "",
            email: user?.email || "",
            phone: user?.phone || user?.number || "",
            number: user?.number || "",
            subject: "",
            message: "",
            agree: false,
          });
        },
        (err) => {
          console.error(err);
        }
      )
      .finally(() => setLoading(false));

  };

  return (
    <div className={styles.page}>
      <motion.div
        className={styles.card}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        <h2 className={styles.heading}>READY TO WORK WITH US</h2>
        <p className={styles.subheading}>
          Contact us for all your questions and opinions
        </p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>First Name *</label>
              <input
                type="text"
                name="first_name"
                placeholder="First Name"
                value={formData.first_name}
                onChange={handleChange}
              />
              {errors.first_name && (
                <span className={styles.error}>{errors.first_name}</span>
              )}
            </div>
          </div>

          <div className={styles.field}>
            <label>Email Address (Optional)</label>
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <span className={styles.error}>{errors.email}</span>
            )}
          </div>

          <div className={styles.field}>
            <label>Phone Number</label>
            <input
              type="text"
              name="phone"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={handleChange}
              readOnly
            />
          </div>

          <div className={styles.field}>
            <label>Subject (Optional)</label>
            <input
              type="text"
              name="subject"
              placeholder="Subject"
              value={formData.subject}
              onChange={handleChange}
            />
          </div>

          <div className={styles.field}>
            <label>Message *</label>
            <textarea
              name="message"
              placeholder="Write your message here..."
              value={formData.message}
              onChange={handleChange}
            ></textarea>
            {errors.message && (
              <span className={styles.error}>{errors.message}</span>
            )}
          </div>

          <div className={styles.checkboxRow}>
            <input
              type="checkbox"
              id="updates"
              name="agree"
              checked={formData.agree}
              onChange={handleChange}
            />
            <label htmlFor="updates">
              I agree to the <Link to="/termCondition">Terms & Conditions</Link>
              .
            </label>
          </div>
          {errors.agree && <span className={styles.error}>{errors.agree}</span>}

          <motion.button
            type="submit"
            className={styles.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={loading}
          >
            {loading ? "Sending..." : "SEND MESSAGE"}
          </motion.button>
        </form>
      </motion.div>
      {/* Map Card */}
      <motion.div
        className={styles.card}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
      >
        <h2 className={styles.heading}>📍 FIND US ON GOOGLE MAP</h2>
        <div className={styles.mapWrapper}>
          <iframe
            title="map"
            src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3488.323342087102!2d77.66556017551575!3d29.037016075446274!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjnCsDAyJzEzLjMiTiA3N8KwNDAnMDUuMyJF!5e0!3m2!1sen!2sin!4v1757412801036!5m2!1sen!2sin"
            width="100%"
            height="300"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </div>
      </motion.div>
    </div>
  );
}
